import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Chip,
    Divider,
    Button,
    CircularProgress,
    Alert,
    Stack,
    TextField,
    Switch,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CategoryIcon from '@mui/icons-material/Category';
import HealingIcon from '@mui/icons-material/Healing';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { shopModel } from '../Axios/AxiosConfig';

const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
    ],
};

interface ProductImage {
    src?: string;
    alt?: string;
    id?: number;
    [key: string]: any;
}

interface GeneralInfo {
    company?: string;
    company_id?: string;
    producttype?: string;
    deliverycharges?: number;
    desc?: string;
    detaildesc?: string;
    disease?: string;
    hindidesc?: string;
    hindidetaildesc?: string;
    hindidisease?: string;
    hindiname?: string;
    imgurl?: string;
    name?: string;
    onsale?: boolean;
    orgprice?: string;
    price?: string;
    product_id?: string;
    product_image_url?: string;
    qty?: string;
    sku?: string;
    subtype?: string;
    variant_id?: number;
    type?: string;
    images?: ProductImage[];
    [key: string]: any;
}

interface KeyIngredient {
    benefits?: string;
    img?: string;
    name?: string;
}

interface KeyBenefit {
    hindiname?: string;
    img?: string;
    name?: string;
}

interface FaqItem {
    answer?: string;
    hindianswer?: string;
    hindiquestion?: string;
    question?: string;
}

interface Product {
    id: string;
    general?: GeneralInfo;
    keyingredients?: KeyIngredient[];
    keybenefits?: KeyBenefit[];
    other_info?: any;
    html?: string;
    topselling?: boolean;
    dosharecommendation?: string;
    images?: { src?: string }[];
    faq?: FaqItem[];
}

const ProductDetailsPage: React.FC = () => {
    const { productId } = useParams<{ productId: string }>();
    const navigate = useNavigate();

    const [product, setProduct] = useState<Product | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pendingMainImageFile, setPendingMainImageFile] = useState<File | null>(null);
    const [pendingImageFiles, setPendingImageFiles] = useState<(File | null)[]>([]);
    const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

    const fetchProduct = async (id: string) => {
        try {
            setLoading(true);
            setError(null);

            const response = await shopModel.post('/getOneProduct', { id });
            const data: any = response?.data;

            let item: any = null;
            if (Array.isArray(data)) {
                item = data[0];
            } else if (Array.isArray(data?.Products)) {
                item = data.Products[0];
            } else if (Array.isArray(data?.data)) {
                item = data.data[0];
            }

            if (!item) {
                setError('Product not found');
                setProduct(null);
                return;
            }

            setProduct(item as Product);
        } catch (err: any) {
            console.error('Error fetching product:', err);
            setError(err?.response?.data?.message || 'Failed to load product details');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (productId) {
            fetchProduct(productId);
        } else {
            setError('No product ID provided');
        }
    }, [productId]);

    useEffect(() => {
        setSelectedImageIndex(null);
    }, [product]);

    const updateGeneralField = <K extends keyof GeneralInfo>(key: K, value: GeneralInfo[K]) => {
        setProduct(prev => {
            if (!prev) return prev;

            const next = {
                ...prev,
                general: {
                    ...(prev.general || {}),
                    [key]: value,
                },
            };

            // If main price is updated and variants exist, update the first variant's price
            if (key === 'price' && typeof value === 'string' && prev.other_info?.variants?.length > 0) {
                const newVariants = [...(prev.other_info.variants || [])];
                newVariants[0] = { ...newVariants[0], price: value };
                next.other_info = {
                    ...(prev.other_info || {}),
                    variants: newVariants,
                };
            }

            return next;
        });
    };

    const handleVariantChange = (index: number, field: string, value: string) => {
        setProduct(prev => {
            if (!prev) return prev;
            const newVariants = [...(prev.other_info?.variants || [])];
            newVariants[index] = { ...newVariants[index], [field]: value };

            const next = {
                ...prev,
                other_info: {
                    ...(prev.other_info || {}),
                    variants: newVariants,
                },
            };

            // If first variant's price is updated, update the main price
            if (index === 0 && field === 'price') {
                next.general = {
                    ...(prev.general || {}),
                    price: value,
                };
            }

            return next;
        });
    };

    const handleAddVariant = () => {
        setProduct(prev => {
            if (!prev) return prev;
            const newVariants = [...(prev.other_info?.variants || []), { title: '', price: '' }];
            return {
                ...prev,
                other_info: {
                    ...(prev.other_info || {}),
                    variants: newVariants,
                },
            };
        });
    };

    const handleRemoveVariant = (index: number) => {
        setProduct(prev => {
            if (!prev) return prev;
            const newVariants = (prev.other_info?.variants || []).filter((_: any, i: number) => i !== index);
            return {
                ...prev,
                other_info: {
                    ...(prev.other_info || {}),
                    variants: newVariants,
                },
            };
        });
    };


    const updateHtml = (value: string) => {
        setProduct(prev => prev ? { ...prev, html: value } : prev);
    };

    const uploadImage = async (file: File): Promise<string | null> => {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await shopModel.post('/UploadImage', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            const data: any = response?.data;
            const url: string | undefined =
                data?.url || data?.Location || data?.location || data?.path || data?.imageUrl;

            if (!url) {
                throw new Error('No image URL returned from upload');
            }

            return url;
        } catch (err) {
            console.error('Error uploading image:', err);
            setError('Failed to upload image');
            return null;
        }
    };

    const handleSave = async () => {
        if (!product) return;

        try {
            setSaving(true);
            setError(null);

            const formData = new FormData();
            formData.append('id', product.id);

            const { images, ...generalRest } = product.general || {};
            const generalPayload: any = {
                ...generalRest,
                product_id: (product.general && product.general.product_id) || product.id,
            };

            formData.append('general', JSON.stringify(generalPayload));

            const imageListForExisting = getImageList(product);

            const existingImages = imageListForExisting
                .filter((img, index) => img?.src && !pendingImageFiles[index])
                .map(img => ({
                    src: img.src as string,
                    alt: img.alt || (product.general?.name || ''),
                }));

            if (existingImages.length) {
                formData.append('existingImages', JSON.stringify(existingImages));
            }

            if (typeof product.html === 'string' && product.html.trim()) {
                formData.append('html', product.html.trim());
            }

            if (Array.isArray(product.keyingredients)) {
                formData.append('keyingredients', JSON.stringify(product.keyingredients));
            }

            if (Array.isArray(product.keybenefits)) {
                formData.append('keybenefits', JSON.stringify(product.keybenefits));
            }

            if (product.other_info && typeof product.other_info === 'object') {
                formData.append('other_info', JSON.stringify(product.other_info));
            }

            if (typeof product.topselling === 'boolean') {
                formData.append('topselling', String(product.topselling));
            }

            if (typeof product.dosharecommendation === 'string') {
                formData.append('dosharecommendation', product.dosharecommendation);
            }

            if (Array.isArray(product.faq)) {
                formData.append('faq', JSON.stringify(product.faq));
            }

            if (pendingMainImageFile) {
                formData.append('images', pendingMainImageFile);
            }

            pendingImageFiles.forEach(file => {
                if (file) {
                    formData.append('images', file);
                }
            });

            const response = await shopModel.post('/UpdateProduct', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });

            if (response.status === 200) {

                const { product, message } = response?.data

                const saved = product as Product | undefined;

                if (!saved) {
                    throw new Error('No product returned from update');
                }

                setProduct(saved);
                setIsEditing(false);

                const savedImagesArray: ProductImage[] =
                    saved.general?.images && saved.general.images.length > 0
                        ? saved.general.images
                        : (saved.images || []);
                setPendingMainImageFile(null);
                setPendingImageFiles(new Array(savedImagesArray.length).fill(null));
            }
        } catch (err: any) {
            console.error('Error updating product:', err);
            setError(err?.response?.data?.message || 'Failed to update product');
        } finally {
            setSaving(false);
        }
    };

    const getImageList = (p: Product | null): ProductImage[] => {
        if (!p) return [];
        if (p.general?.images && p.general.images.length > 0) {
            return p.general.images;
        }
        return p.images || [];
    };

    const updateImageSrc = (index: number, src: string) => {
        setProduct(prev => {
            if (!prev) return prev;

            const useGeneral = !!(prev.general?.images && prev.general.images.length > 0) || !prev.images;
            const current: ProductImage[] = useGeneral
                ? (prev.general?.images || [])
                : (prev.images || []);

            if (index < 0 || index >= current.length) return prev;

            const newImages = current.map((img, i) => (i === index ? { ...img, src } : img));

            if (useGeneral) {
                return {
                    ...prev,
                    general: {
                        ...(prev.general || {}),
                        images: newImages,
                    },
                };
            }

            return {
                ...prev,
                images: newImages,
            };
        });

        setPendingImageFiles(prev => prev.map((file, i) => (i === index ? null : file)));
    };

    const addImage = () => {
        setProduct(prev => {
            if (!prev) return prev;

            const useGeneral = !!(prev.general?.images && prev.general.images.length > 0) || !prev.images;
            const current: ProductImage[] = useGeneral
                ? (prev.general?.images || [])
                : (prev.images || []);

            const newImages = [...current, { src: '' }];

            if (useGeneral) {
                return {
                    ...prev,
                    general: {
                        ...(prev.general || {}),
                        images: newImages,
                    },
                };
            }

            return {
                ...prev,
                images: newImages,
            };
        });

        setPendingImageFiles(prev => [...prev, null]);
    };

    const removeImage = (index: number) => {
        setProduct(prev => {
            if (!prev) return prev;

            const useGeneral = !!(prev.general?.images && prev.general.images.length > 0) || !prev.images;
            const current: ProductImage[] = useGeneral
                ? (prev.general?.images || [])
                : (prev.images || []);

            if (index < 0 || index >= current.length) return prev;

            const newImages = current.filter((_, i) => i !== index);

            if (useGeneral) {
                return {
                    ...prev,
                    general: {
                        ...(prev.general || {}),
                        images: newImages,
                    },
                };
            }

            return {
                ...prev,
                images: newImages,
            };
        });

        setPendingImageFiles(prev => prev.filter((_, i) => i !== index));
    };

    const imageList = getImageList(product);

    const mainImage =
        (selectedImageIndex !== null && imageList[selectedImageIndex]?.src) ||
        product?.general?.imgurl ||
        product?.general?.product_image_url ||
        (imageList[0] && imageList[0].src) ||
        '';

    const displayPrice = product?.general ? product.general.price || product.general.orgprice : '';

    return (
        <Box sx={{ p: 3, pt: 1.5, minHeight: '100vh' }}>
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(-1)}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        Back
                    </Button>
                    <Box>
                        <Typography
                            variant="h6"
                            component="h6"
                            sx={{ fontWeight: 600, fontFamily: 'Libre Baskerville', color: '#3B362A' }}
                        >
                            Product Details
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            View full information about this product
                        </Typography>
                    </Box>
                </Box>
                {product && (
                    <Button
                        variant="contained"
                        size="small"
                        disabled={saving}
                        onClick={isEditing ? handleSave : () => setIsEditing(true)}
                        sx={{ textTransform: 'none', borderRadius: 2 }}
                    >
                        {isEditing ? (saving ? 'Saving...' : 'Done') : 'Edit'}
                    </Button>
                )}
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}

            {!loading && product && (
                <Grid container spacing={4}>
                    <Grid item xs={12} md={5}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
                                overflow: 'hidden',
                            }}
                        >
                            {mainImage ? (
                                <Box
                                    sx={{
                                        background: 'linear-gradient(45deg, #f4ecd6 30%, #f8f8f4 90%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 3,
                                        overflow: 'hidden',
                                    }}
                                >
                                    <CardMedia
                                        component="img"
                                        image={
                                            pendingMainImageFile
                                                ? URL.createObjectURL(pendingMainImageFile)
                                                : mainImage
                                        }
                                        alt={product.general?.name || 'Product image'}
                                        sx={{
                                            maxHeight: 320,
                                            width: 'auto',
                                            objectFit: 'contain',
                                            mixBlendMode: 'multiply',
                                            transition: 'transform 0.3s ease',
                                            cursor: 'zoom-in',
                                            '&:hover': {
                                                transform: 'scale(1.08)',
                                            },
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    sx={{
                                        height: 280,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'linear-gradient(45deg, #f4ecd6 30%, #f8f8f4 90%)',
                                    }}
                                >
                                    <Typography variant="subtitle2" color="text.secondary">
                                        No image available
                                    </Typography>
                                </Box>
                            )}

                            {isEditing && (
                                <CardContent sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        component="label"
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Change Main Image
                                        <input
                                            type="file"
                                            accept="image/*"
                                            hidden
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;
                                                setPendingMainImageFile(file);
                                                e.target.value = '';
                                            }}
                                        />
                                    </Button>
                                </CardContent>
                            )}

                            {(isEditing || product.general?.qty) && (
                                <CardContent sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                                        <Typography variant="body2" color="text.secondary">
                                            Pack Size
                                        </Typography>
                                        {isEditing ? (
                                            <TextField
                                                size="small"
                                                value={product.general?.qty || ''}
                                                onChange={(e) => updateGeneralField('qty', e.target.value)}
                                            />
                                        ) : (
                                            <Chip
                                                label={product.general?.qty ? product.general.qty.trim() : ''}
                                                size="small"
                                                sx={{ fontWeight: 600 }}
                                            />
                                        )}
                                    </Stack>
                                </CardContent>
                            )}
                            {imageList && imageList.length > 1 && (
                                <CardContent sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        More Images
                                    </Typography>
                                    <Stack direction="row" spacing={1} sx={{ overflowX: 'auto', pb: 0.5 }}>
                                        {imageList.map((img, index) => (
                                            img?.src && (
                                                <Box
                                                    key={img.id ?? img.src ?? index}
                                                    onClick={() => setSelectedImageIndex(index)}
                                                    sx={{
                                                        borderRadius: 1,
                                                        border: selectedImageIndex === index ? '2px solid' : '1px solid',
                                                        borderColor:
                                                            selectedImageIndex === index ? 'primary.main' : 'divider',
                                                        overflow: 'hidden',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <CardMedia
                                                        component="img"
                                                        image={img.src}
                                                        alt={product.general?.name || `Image ${index + 1}`}
                                                        sx={{
                                                            width: 72,
                                                            height: 72,
                                                            objectFit: 'cover',
                                                            transition: 'transform 0.2s ease',
                                                            '&:hover': {
                                                                transform: 'scale(1.05)',
                                                            },
                                                        }}
                                                    />
                                                </Box>
                                            )
                                        ))}
                                    </Stack>
                                </CardContent>
                            )}
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={7}>
                        <Card
                            sx={{
                                borderRadius: 3,
                                boxShadow: '0 6px 24px rgba(0,0,0,0.12)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                {isEditing ? (
                                    <Box>
                                        <TextField
                                            label="Name"
                                            fullWidth
                                            size="small"
                                            value={product.general?.name || ''}
                                            onChange={(e) => updateGeneralField('name', e.target.value)}
                                            sx={{ mb: 2 }}
                                        />
                                        <Grid container spacing={2} sx={{ mb: 2 }}>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Company"
                                                    fullWidth
                                                    size="small"
                                                    value={product.general?.company || ''}
                                                    onChange={(e) => updateGeneralField('company', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Product Type"
                                                    fullWidth
                                                    size="small"
                                                    value={product.general?.producttype || ''}
                                                    onChange={(e) => updateGeneralField('producttype', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Category"
                                                    fullWidth
                                                    size="small"
                                                    value={product.general?.type || ''}
                                                    onChange={(e) => updateGeneralField('type', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    label="Disease"
                                                    fullWidth
                                                    size="small"
                                                    value={product.general?.disease || ''}
                                                    onChange={(e) => updateGeneralField('disease', e.target.value)}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ) : (
                                    <Box>
                                        <Typography
                                            variant="h5"
                                            sx={{ fontWeight: 700, mb: 1, letterSpacing: 0.1 }}
                                        >
                                            {product.general?.name}
                                        </Typography>

                                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mb: 2 }}>
                                            {product.general?.company && (
                                                <Chip
                                                    label={product.general.company}
                                                    size="small"
                                                    icon={<LocalOfferIcon fontSize="small" />}
                                                    sx={{ backgroundColor: '#f3e5f5', color: '#4a148c' }}
                                                />
                                            )}

                                            {product.general?.producttype && (
                                                <Chip
                                                    label={product.general.producttype}
                                                    size="small"
                                                    icon={<CategoryIcon fontSize="small" />}
                                                    sx={{ backgroundColor: '#e3f2fd', color: '#0d47a1' }}
                                                />
                                            )}

                                            {product.general?.type && (
                                                <Chip
                                                    label={product.general.type}
                                                    size="small"
                                                    icon={<HealingIcon fontSize="small" />}
                                                    sx={{ backgroundColor: '#e8f5e9', color: '#1b5e20' }}
                                                />
                                            )}

                                            {product.general?.disease && (
                                                <Chip
                                                    label={product.general.disease}
                                                    size="small"
                                                    sx={{ backgroundColor: '#fff3e0', color: '#e65100' }}
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                )}

                                <Divider sx={{ my: 2 }} />

                                <Stack
                                    direction="row"
                                    spacing={3}
                                    alignItems="center"
                                    flexWrap="wrap"
                                    sx={{ mb: 2 }}
                                >
                                    <Box sx={{ minWidth: 180 }}>
                                        <Typography variant="overline" color="text.secondary">
                                            Price
                                        </Typography>
                                        {isEditing ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={product.general?.price || ''}
                                                onChange={(e) => updateGeneralField('price', e.target.value)}
                                            />
                                        ) : (
                                            <Typography
                                                variant="h5"
                                                sx={{ fontWeight: 700, color: '#2e7d32' }}
                                            >
                                                ₹{displayPrice}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box sx={{ minWidth: 160 }}>
                                        <Typography variant="overline" color="text.secondary">
                                            MRP
                                        </Typography>
                                        {isEditing ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={product.general?.orgprice || ''}
                                                onChange={(e) => updateGeneralField('orgprice', e.target.value)}
                                            />
                                        ) : (
                                            product.general?.orgprice && product.general.orgprice !== product.general.price && (
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        textDecoration: 'line-through',
                                                        color: 'text.secondary',
                                                    }}
                                                >
                                                    ₹{product.general.orgprice}
                                                </Typography>
                                            )
                                        )}
                                    </Box>

                                    <Box sx={{ minWidth: 160 }}>
                                        <Typography variant="overline" color="text.secondary">
                                            Availability
                                        </Typography>
                                        {isEditing ? (
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                <Switch
                                                    checked={!!product.general?.onsale}
                                                    onChange={(_, checked) => updateGeneralField('onsale', checked)}
                                                    size="small"
                                                />
                                                <Typography variant="body2">
                                                    {product.general?.onsale ? 'In Stock' : 'Out of Stock'}
                                                </Typography>
                                            </Stack>
                                        ) : (
                                            <Box sx={{ mt: 0.5 }}>
                                                {product.general?.onsale ? (
                                                    <Chip
                                                        label="In Stock"
                                                        size="small"
                                                        color="success"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="Out of Stock"
                                                        size="small"
                                                        color="error"
                                                        sx={{ fontWeight: 600 }}
                                                    />
                                                )}
                                            </Box>
                                        )}
                                    </Box>

                                    <Box sx={{ minWidth: 200 }}>
                                        <Typography variant="overline" color="text.secondary">
                                            Delivery Charges
                                        </Typography>
                                        {isEditing ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                type="number"
                                                value={
                                                    product.general?.deliverycharges !== undefined
                                                        ? String(product.general.deliverycharges)
                                                        : ''
                                                }
                                                onChange={(e) => {
                                                    const value = e.target.value;
                                                    const num = value === '' ? undefined : Number(value);
                                                    updateGeneralField('deliverycharges', num as number | undefined);
                                                }}
                                            />
                                        ) : (
                                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                                                <LocalShippingIcon fontSize="small" color="action" />
                                                <Typography variant="body2">
                                                    {product.general?.deliverycharges === 0
                                                        ? 'Free delivery'
                                                        : `₹${product.general?.deliverycharges}`}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </Box>
                                </Stack>

                                {(isEditing || product.general?.desc) && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            Description
                                        </Typography>
                                        {isEditing ? (
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                size="small"
                                                value={product.general?.desc || ''}
                                                onChange={(e) => updateGeneralField('desc', e.target.value)}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {product.general?.desc}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {(isEditing || product.general?.detaildesc) && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            Details
                                        </Typography>
                                        {isEditing ? (
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={3}
                                                size="small"
                                                value={product.general?.detaildesc || ''}
                                                onChange={(e) => updateGeneralField('detaildesc', e.target.value)}
                                            />
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                                                {product.general?.detaildesc}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {(isEditing || product.html) && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                                            More Information
                                        </Typography>
                                        {isEditing ? (
                                            <ReactQuill
                                                theme="snow"
                                                value={product.html || ''}
                                                onChange={(value: string) => updateHtml(value)}
                                                modules={quillModules}
                                                style={{ height: 220, marginTop: 8 }}
                                            />
                                        ) : (
                                            <Box
                                                sx={{
                                                    '& p': { mb: 1 },
                                                    '& ul': { pl: 3, mb: 1.5 },
                                                    '& li': { mb: 0.5 },
                                                }}
                                                dangerouslySetInnerHTML={{ __html: product.html || '' }}
                                            />
                                        )}
                                    </Box>
                                )}

                                <Divider sx={{ my: 4 }} />

                                <Box sx={{ mt: 2 }}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            Variants (Pricing)
                                        </Typography>
                                        {isEditing && (
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={handleAddVariant}
                                                sx={{ textTransform: 'none' }}
                                            >
                                                Add Variant
                                            </Button>
                                        )}
                                    </Stack>
                                    {(product.other_info?.variants || []).map((v: any, index: number) => (
                                        <Grid container spacing={2} key={index} sx={{ mb: 1, alignItems: 'center' }}>
                                            <Grid item xs={5}>
                                                <TextField
                                                    label="Title (e.g. 400 GM)"
                                                    fullWidth
                                                    size="small"
                                                    disabled={!isEditing}
                                                    value={v.title || ''}
                                                    onChange={(e) => handleVariantChange(index, 'title', e.target.value)}
                                                />
                                            </Grid>
                                            <Grid item xs={isEditing ? 5 : 7}>
                                                <TextField
                                                    label="Price (₹)"
                                                    fullWidth
                                                    size="small"
                                                    type="number"
                                                    disabled={!isEditing}
                                                    value={v.price || ''}
                                                    onChange={(e) => handleVariantChange(index, 'price', e.target.value)}
                                                />
                                            </Grid>
                                            {isEditing && (
                                                <Grid item xs={2}>
                                                    <Button
                                                        color="error"
                                                        onClick={() => handleRemoveVariant(index)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    ))}
                                    {(!product.other_info?.variants || product.other_info.variants.length === 0) && (
                                        <Typography variant="body2" color="text.secondary">
                                            No variants available.
                                        </Typography>
                                    )}
                                </Box>



                                {(isEditing || product.general?.subtype) && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Tags
                                        </Typography>
                                        {isEditing ? (
                                            <TextField
                                                fullWidth
                                                size="small"
                                                value={product.general?.subtype || ''}
                                                onChange={(e) => updateGeneralField('subtype', e.target.value)}
                                                placeholder="Comma-separated tags"
                                            />
                                        ) : (
                                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                                {product.general?.subtype &&
                                                    product.general.subtype.split(',').map((tag) => (
                                                        <Chip
                                                            key={tag.trim()}
                                                            label={tag.trim()}
                                                            size="small"
                                                            sx={{ mb: 0.5 }}
                                                        />
                                                    ))}
                                            </Stack>
                                        )}
                                    </Box>
                                )}

                                {isEditing && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                            Images
                                        </Typography>
                                        <Stack spacing={1.5}>
                                            {imageList.map((img, index) => (
                                                <Box key={img.id ?? img.src ?? index}>
                                                    <Stack
                                                        direction="row"
                                                        spacing={1}
                                                        alignItems="center"
                                                    >
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label={`Image ${index + 1}`}
                                                            value={img.src || ''}
                                                            onChange={(e) => updateImageSrc(index, e.target.value)}
                                                        />
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            component="label"
                                                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                                        >
                                                            Upload
                                                            <input
                                                                type="file"
                                                                accept="image/*"
                                                                hidden
                                                                onChange={(e) => {
                                                                    const file = e.target.files?.[0];
                                                                    if (!file) return;
                                                                    setPendingImageFiles(prev => {
                                                                        const next = [...prev];
                                                                        if (next.length <= index) {
                                                                            next.length = index + 1;
                                                                        }
                                                                        next[index] = file;
                                                                        return next;
                                                                    });
                                                                    e.target.value = '';
                                                                }}
                                                            />
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            size="small"
                                                            onClick={() => removeImage(index)}
                                                            sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                                                        >
                                                            Remove
                                                        </Button>
                                                    </Stack>
                                                    {(pendingImageFiles[index] || img.src) && (
                                                        <Box
                                                            sx={{
                                                                mt: 0.5,
                                                                display: 'inline-block',
                                                                borderRadius: 1,
                                                                border: '1px solid',
                                                                borderColor: 'divider',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <CardMedia
                                                                component="img"
                                                                image={
                                                                    pendingImageFiles[index]
                                                                        ? URL.createObjectURL(pendingImageFiles[index] as File)
                                                                        : img.src || ''
                                                                }
                                                                alt={product.general?.name || `Image ${index + 1} preview`}
                                                                sx={{
                                                                    width: 72,
                                                                    height: 72,
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        </Box>
                                                    )}
                                                </Box>
                                            ))}
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={addImage}
                                                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                                            >
                                                Add Image
                                            </Button>
                                        </Stack>
                                    </Box>
                                )}

                                {product.keyingredients && product.keyingredients.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                            Key Ingredients
                                        </Typography>
                                        <Stack spacing={1.5}>
                                            {product.keyingredients.map((ing, index) => (
                                                <Stack key={index} direction="row" spacing={1.5} alignItems="flex-start">
                                                    {ing.img && (
                                                        <CardMedia
                                                            component="img"
                                                            image={ing.img}
                                                            alt={ing.name || `Ingredient ${index + 1}`}
                                                            sx={{
                                                                width: 48,
                                                                height: 48,
                                                                objectFit: 'cover',
                                                                borderRadius: 1,
                                                            }}
                                                        />
                                                    )}
                                                    <Box>
                                                        {ing.name && (
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {ing.name}
                                                            </Typography>
                                                        )}
                                                        {ing.benefits && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {ing.benefits}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}

                                {product.keybenefits && product.keybenefits.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                            Key Benefits
                                        </Typography>
                                        <Stack spacing={1.5}>
                                            {product.keybenefits.map((benefit, index) => (
                                                <Stack key={index} direction="row" spacing={1.5} alignItems="center">
                                                    {benefit.img && (
                                                        <CardMedia
                                                            component="img"
                                                            image={benefit.img}
                                                            alt={benefit.name || `Benefit ${index + 1}`}
                                                            sx={{
                                                                width: 40,
                                                                height: 40,
                                                                objectFit: 'cover',
                                                                borderRadius: '50%',
                                                            }}
                                                        />
                                                    )}
                                                    <Box>
                                                        {benefit.name && (
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {benefit.name}
                                                            </Typography>
                                                        )}
                                                        {benefit.hindiname && (
                                                            <Typography variant="body2" color="text.secondary">
                                                                {benefit.hindiname}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}

                                {product.faq && product.faq.length > 0 && (
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                            FAQs
                                        </Typography>
                                        <Stack spacing={1.5}>
                                            {product.faq.map((item, index) => (
                                                <Box key={index}>
                                                    {item.question && (
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {item.question}
                                                        </Typography>
                                                    )}
                                                    {item.answer && (
                                                        <Typography variant="body2" color="text.secondary">
                                                            {item.answer}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )
            }
        </Box >
    );
};

export default ProductDetailsPage;
